/* eslint-disable */
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getCourseById, getCourseWithProgress, type Course } from '../../api/courcesService';
import CourseDetailHeader from '../../components/Header/CourseDetailHeader';
import CourseDetail from './CourseDetail';
import './CourseDetail.css';
import {useAppState} from "../../store/useAppState";

// Create lightweight fake placeholders to use while data is loading
const createFakeCourse = (courseId: number): Course => ({
  id: courseId,
  title: 'Loading... ',
  description: '',
  chapters: [],
  totalLessons: 0,
  lessonsCompleted: 0,
  isNew: false,
  progressMessages: [
    { id: 0, title: 'Preparing your course', description: 'Please wait a moment', percentProgress: 0 },
  ],
});

const createFakeCourseWithProgress = (courseId: number): Course => ({
  ...createFakeCourse(courseId),
  progressInfo: {
    id: 0,
    documentId: 'loading',
    completed: false,
    progressPercentage: 0,
    startedAt: undefined,
    completedAt: undefined,
    timeSpent: 0,
    lastAccessedAt: undefined,
    fragmentIndex: 0,
    fragmentProgress: {},
    lesson: {
      id: 0,
      documentId: '0',
      title: 'Loading lesson',
      content: '',
      order: 0,
      slug: 'loading',
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      publishedAt: null,
      locale: null,
    },
    chapter: {
      id: 0,
      documentId: '0',
      title: 'Loading chapter',
      description: '',
      order: 0,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      publishedAt: null,
      locale: null,
    },
    startedLessons: [],
    finishedLessons: [],
  },
});

const CourseDetailWrapper: React.FC = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [course, setCourse] = useState<Course | null>(null);
  const [courseWithProgress, setCourseWithProgress] = useState<Course | null>(null);
  const { setLoading, loading } = useAppState((state) => state);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchCourseAndProgress = async () => {
      if (!id) {
        setError('Course ID is required');
        setLoading(false);
        return;
      }
      try {
        setLoading(true);

        // Step 1: Get course structure (basic course data)
        const courseData = await getCourseById(parseInt(id));
        setCourse(courseData);

        // Step 2: Get course with progress (user-specific progress data)
        const courseProgressData = await getCourseWithProgress(parseInt(id));
        setCourseWithProgress(courseProgressData);

      } catch (err) {
        console.error('Error fetching course or progress:', err);
        setError('Failed to load course details');
      } finally {
        setLoading(false);
      }
    };
    fetchCourseAndProgress();
  }, [id]); //eslint-disable-line

  // Calculate progress percentage based on started and finished lessons
  const calculateOverallProgress = (): number => {
    if (!course || !course.chapters || !courseWithProgress || !courseWithProgress.progressInfo) return 0;

    let totalLessons = 0;
    let finishedLessons = 0;

    // Calculate total lessons
    course.chapters?.forEach(chapter => {
      if (chapter.lessons) {
        totalLessons += chapter.lessons.length;
      }
    });

    // Use progressInfo to get finished lessons count
    finishedLessons = courseWithProgress.progressInfo.finishedLessons.length;

    // Calculate progress based on finished lessons
    return totalLessons > 0 ? Math.round((finishedLessons / totalLessons) * 100) : 0;
  };

  // Check if user has activity today (simplified logic - you can enhance this)
  const hasActivityToday = (): boolean => {
    if (!courseWithProgress || !courseWithProgress.progressInfo) return false;

    // Check if there are any started or finished lessons
    const hasStartedLessons = courseWithProgress.progressInfo.startedLessons.length > 0;
    const hasFinishedLessons = courseWithProgress.progressInfo.finishedLessons.length > 0;

    return hasStartedLessons || hasFinishedLessons;
  };

  const progressPercent = calculateOverallProgress();
  const hasActivity = hasActivityToday();

  // if (loading) {
  //   return (
  //     <div className="course-detail-container">
  //       <CourseDetailHeader
  //         courseTitle="Loading..."
  //         progressPercent={0}
  //         hasActivityToday={false}
  //       />
  //       <div className="course-detail-loading">Loading course details...</div>
  //     </div>
  //   );
  // }


  if ((error || !course) && !loading) {
    return (
      <div className="course-detail-container">
        <CourseDetailHeader
          courseTitle="Error"
          progressPercent={0}
          hasActivityToday={false}
        />
        <div className="course-detail-error">
          {error || 'Course not found'}
          <button onClick={() => navigate(-1)}>Go Back</button>
        </div>
      </div>
    );
  }

  const numericId = Number(id) || 0;
  const safeCourse = course ?? createFakeCourse(numericId);
  const safeCourseWithProgress = courseWithProgress ?? createFakeCourseWithProgress(numericId);

  return (
    <div className="course-detail-container">
      <CourseDetailHeader
        courseTitle={safeCourse?.title || ''}
        progressPercent={progressPercent}
        hasActivityToday={hasActivity}
      />

      {/* Loading skeleton for chapters */}
      {loading && (
        <div className="course-detail-levels">
          {[0, 1].map((chapterIdx) => (
            <div key={chapterIdx} className="course-detail-chapter">
              <div className="course-detail-level-header">
                <div className="course-detail-level-title skeleton skeleton-text" style={{ width: '60%', height: 20 }} />
                <div className="course-detail-level-chevron">
                  <div className="skeleton skeleton-circle" style={{ width: 20, height: 20 }} />
                </div>
              </div>
              <div className="course-detail-levels-list">
                {[0, 1, 2].map((i) => (
                  <div key={i} className="course-detail-level-item">
                    <div className="course-detail-lesson-content">
                      <div className="skeleton skeleton-text" style={{ width: i === 0 ? '80%' : i === 1 ? '65%' : '50%', height: 16 }} />
                    </div>
                    <div className="course-detail-lesson-icon">
                      <div className="skeleton skeleton-circle" style={{ width: 24, height: 24 }} />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Always render content but provide safe placeholders while loading */}
        <CourseDetail course={safeCourse} courseWithProgress={safeCourseWithProgress} />
    </div>
  );
};

export default CourseDetailWrapper;
